<template>
  <v-navigation-drawer
    :key="`feedback-drawer-${currentLocale}`"
    :model-value="modelValue"
    location="right"
    :width="450"
    :temporary="true"
    :scrim="true"
    class="feedback-drawer"
    :class="[
      { 'mobile-bottom-drawer': smAndDown },
      {
        'top-38': hasNotifications && smAndUp,
      },
    ]"
    :color="isDarkTheme ? '#1e1e1e' : '#fff'"
    @update:model-value="onDrawerChange"
  >
    <!-- Drawer header -->
    <v-toolbar
      :elevation="3"
      :color="isDarkTheme ? '#2c2c2c' : '#fff'"
      :class="isDarkTheme ? 'text-white' : 'text-black'"
      class="pr-4"
    >
      <v-btn icon @click="closeDrawer">
        <v-icon icon="close" size="small" />
      </v-btn>

      <v-toolbar-title class="text-body-1 pl-0 ml-0">
        {{ $t('feedback.formTitle') }}
      </v-toolbar-title>

      <v-spacer></v-spacer>
    </v-toolbar>

    <!-- Form content -->
    <v-container class="pa-4">
      <v-form ref="form" v-model="valid">
        <!-- Name -->
        <v-text-field
          v-model="formData.name"
          :label="$t('feedback.name')"
          :rules="[rules.required]"
          variant="outlined"
          density="compact"
          class="mb-2"
        />

        <!-- Email -->
        <v-text-field
          v-model="formData.email"
          :label="$t('feedback.email')"
          type="email"
          :rules="[rules.required, rules.email]"
          variant="outlined"
          density="compact"
          class="mb-2"
        />

        <!-- Category -->
        <v-select
          v-model="formData.category"
          :items="categories"
          :label="$t('feedback.category')"
          :rules="[rules.required]"
          variant="outlined"
          density="compact"
          class="mb-2"
        />

        <!-- Rating -->
        <div class="mb-4">
          <label class="text-caption text-medium-emphasis mb-2 d-block">
            {{ $t('feedback.rating') }}
          </label>
          <v-rating
            v-model="formData.rating"
            color="primary"
            density="compact"
            size="large"
          />
        </div>

        <!-- Description -->
        <v-textarea
          v-model="formData.description"
          :label="$t('feedback.description')"
          :rules="[rules.required]"
          variant="outlined"
          rows="4"
          class="mb-2"
        />

        <!-- Screenshot upload -->
        <v-file-input
          v-model="formData.screenshot"
          :label="$t('feedback.screenshot')"
          accept="image/*"
          variant="outlined"
          density="compact"
          prepend-icon=""
          prepend-inner-icon="mdi-camera"
          class="mb-4"
          @update:model-value="handleScreenshotUpload"
        />

        <!-- Auto-detected info (read-only) -->
        <v-expansion-panels variant="accordion" class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <span class="text-caption">{{ $t('feedback.systemInfo') }}</span>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="text-caption">
                <div><strong>{{ $t('feedback.browser') }}:</strong> {{ systemInfo.browser }}</div>
                <div><strong>{{ $t('feedback.os') }}:</strong> {{ systemInfo.os }}</div>
                <div><strong>{{ $t('feedback.screenResolution') }}:</strong> {{ systemInfo.screenResolution }}</div>
                <div><strong>{{ $t('feedback.viewportSize') }}:</strong> {{ systemInfo.viewportSize }}</div>
                <div><strong>{{ $t('feedback.deviceType') }}:</strong> {{ systemInfo.deviceType }}</div>
                <div><strong>{{ $t('feedback.touchSupport') }}:</strong> {{ systemInfo.hasTouch }}</div>
                <div><strong>{{ $t('feedback.zoomLevel') }}:</strong> {{ systemInfo.zoomLevel }} <span class="text-caption text-medium-emphasis">({{ $t('feedback.approximate') }})</span></div>
                <div><strong>{{ $t('feedback.timezone') }}:</strong> {{ systemInfo.timezone }}</div>
                <div><strong>{{ $t('feedback.theme') }}:</strong> {{ systemInfo.theme }}</div>
                <div><strong>{{ $t('feedback.language') }}:</strong> {{ systemInfo.language }}</div>
                <div v-if="systemInfo.pageContext"><strong>{{ $t('feedback.pageContext') }}:</strong> {{ systemInfo.pageContext }}</div>
                <div><strong>{{ $t('feedback.pageUrl') }}:</strong> {{ systemInfo.page }}</div>
                <div><strong>{{ $t('feedback.route') }}:</strong> {{ systemInfo.route }}</div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Submit button -->
        <v-btn
          color="primary"
          block
          :loading="submitting"
          :disabled="!valid || submitting"
          @click="submitFeedback"
        >
          {{ $t('feedback.submit') }}
        </v-btn>
      </v-form>
    </v-container>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex'
import { detectBrowserOS } from '@/helpers/detectBrowserOS'
import Swal from 'sweetalert2'
import db from '@/firebase/init'
import firebase from 'firebase'

export default {
  name: 'FeedbackDrawer',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      valid: false,
      submitting: false,
      formData: {
        name: '',
        email: '',
        category: null,
        rating: 0,
        description: '',
        screenshot: null,
        screenshotBase64: null,
      },
      rules: {
        required: (value) => !!value || this.$t('feedback.validation.required'),
        email: (value) => {
          const pattern =
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
          return pattern.test(value) || this.$t('feedback.validation.email')
        },
      },
      systemInfo: {
        browser: 'Unknown',
        os: 'Unknown',
        screenResolution: '',
        viewportSize: '',
        deviceType: 'Desktop',
        hasTouch: false,
        zoomLevel: 100,
        timezone: '',
        theme: 'light',
        language: 'en',
        page: '',
        route: '',
        pageContext: null,
      },
    }
  },
  computed: {
    ...mapGetters('UserData', ['getUser']),
    ...mapGetters('AppData', ['getNotificationFeedback']),
    isDarkTheme() {
      return this.$vuetify.theme.current.dark
    },
    smAndDown() {
      return this.$vuetify.display.smAndDown
    },
    smAndUp() {
      return this.$vuetify.display.smAndUp
    },
    hasNotifications() {
      return this.getNotificationFeedback && this.getNotificationFeedback.length > 0
    },
    currentLocale() {
      return this.$i18n.locale || 'en'
    },
    categories() {
      return [
        { title: this.$t('feedback.categoryBug'), value: 'bug' },
        { title: this.$t('feedback.categoryFeature'), value: 'feature' },
        { title: this.$t('feedback.categoryGeneral'), value: 'general' },
      ]
    },
  },
  watch: {
    modelValue(newVal, oldVal) {
      // Prevent opening if ANY unit drawer is open
      if (newVal && !oldVal) {
        const notesOpen = this.$store?.getters?.['AppData/notesOpen'] || false
        const highlightsOpen = this.$store?.getters?.['AppData/highlightsOpen'] || false
        const commentsOpen = this.$store?.getters?.['AppData/commentsOpen'] || false
        if (notesOpen || highlightsOpen || commentsOpen) {
          // Force close immediately
          this.$nextTick(() => {
            this.$emit('update:modelValue', false)
          })
          return
        }
        this.initializeForm()
      }
    },
    '$i18n.locale'() {
      // Force component to update when locale changes
      this.$forceUpdate()
    },
  },
  mounted() {
    this.detectSystemInfo()
  },
  methods: {
    initializeForm() {
      // Auto-fill email from user if available
      if (this.getUser && this.getUser.email) {
        this.formData.email = this.getUser.email
      }
      // Auto-fill name if available
      if (this.getUser && this.getUser.user_name) {
        this.formData.name = this.getUser.user_name
      }
      // Update system info
      this.detectSystemInfo()
    },
    detectSystemInfo() {
      const detected = detectBrowserOS()
      
      // Get page context (course/section/unit if applicable)
      let pageContext = null
      const route = this.$route
      if (route.params.courseID) {
        pageContext = {
          courseID: route.params.courseID,
        }
        if (route.params.sectionID) {
          pageContext.sectionID = route.params.sectionID
        }
        if (route.params.id) {
          pageContext.unitID = route.params.id
        }
      }

      this.systemInfo = {
        browser: detected.browser,
        os: detected.os,
        screenResolution: detected.screenResolution,
        viewportSize: detected.viewportSize,
        deviceType: detected.deviceType,
        hasTouch: detected.hasTouch ? 'Yes' : 'No',
        zoomLevel: `${detected.zoomLevel}%`,
        timezone: detected.timezone,
        theme: this.isDarkTheme ? 'dark' : 'light',
        language: this.$i18n.locale || 'en',
        page: window.location.href,
        route: route.fullPath || route.path, // Use fullPath to include locale prefix if present
        pageContext: pageContext
          ? `${pageContext.courseID}${pageContext.sectionID ? ` / ${pageContext.sectionID}` : ''}${pageContext.unitID ? ` / ${pageContext.unitID}` : ''}`
          : null,
      }
    },
    handleScreenshotUpload(file) {
      if (file) {
        // v-file-input returns a single File object, not an array
        const reader = new FileReader()
        reader.onload = (e) => {
          this.formData.screenshotBase64 = e.target.result
        }
        reader.onerror = (error) => {
          console.error('Error reading file:', error)
          this.formData.screenshotBase64 = null
        }
        reader.readAsDataURL(file)
      } else {
        this.formData.screenshotBase64 = null
      }
    },
    async submitFeedback() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.submitting = true

      try {
        // Rate limiting check - Check localStorage first (quick check)
        const lastSubmissionKey = `feedback_last_submission_${this.formData.email}`
        const lastSubmissionTime = localStorage.getItem(lastSubmissionKey)
        const now = Date.now()
        const RATE_LIMIT_MS = 2 * 60 * 1000 // 2 minutes in milliseconds

        if (lastSubmissionTime) {
          const timeSinceLastSubmission = now - parseInt(lastSubmissionTime)
          if (timeSinceLastSubmission < RATE_LIMIT_MS) {
            const minutesRemaining = Math.ceil(
              (RATE_LIMIT_MS - timeSinceLastSubmission) / 60000
            )
            Swal.fire({
              icon: 'warning',
              title: this.$t('feedback.rateLimit.title'),
              text: this.$t('feedback.rateLimit.message').replace('{minutes}', minutesRemaining),
            })
            this.submitting = false
            return
          }
        }

        // Rate limiting check - Check Firestore for recent submissions (more robust)
        // Note: This requires a Firestore composite index on (email, createdAt)
        // If index doesn't exist, we'll catch the error and rely on localStorage check
        try {
          const twoMinutesAgo = new Date(now - RATE_LIMIT_MS)
          const recentSubmissions = await db
            .collection('feedback')
            .where('email', '==', this.formData.email)
            .where('createdAt', '>', twoMinutesAgo)
            .get()

          if (!recentSubmissions.empty) {
            Swal.fire({
              icon: 'warning',
              title: this.$t('feedback.rateLimit.title'),
              text: this.$t('feedback.rateLimit.recent'),
            })
            this.submitting = false
            return
          }
        } catch (indexError) {
          // If composite index doesn't exist, log warning but continue with localStorage check
          // The localStorage check above should catch most cases
          if (indexError.code === 'failed-precondition') {
            console.warn('Firestore composite index not found. Rate limiting will rely on localStorage check only.')
            // Continue with submission - localStorage check should be sufficient
          } else {
            // Re-throw if it's a different error
            throw indexError
          }
        }

        // Get user ID if available
        const user = firebase.auth().currentUser
        const userId = user ? user.uid : null

        // Prepare form data for submission
        const submissionData = {
          name: this.formData.name,
          email: this.formData.email,
          userId: userId,
          category: this.formData.category,
          rating: this.formData.rating,
          description: this.formData.description,
          screenshot: this.formData.screenshotBase64,
          // System Information
          browser: this.systemInfo.browser,
          os: this.systemInfo.os,
          screenResolution: this.systemInfo.screenResolution,
          viewportSize: this.systemInfo.viewportSize,
          deviceType: this.systemInfo.deviceType,
          hasTouch: this.systemInfo.hasTouch,
          zoomLevel: this.systemInfo.zoomLevel,
          timezone: this.systemInfo.timezone,
          theme: this.systemInfo.theme,
          language: this.systemInfo.language,
          pageContext: this.systemInfo.pageContext,
          page: this.systemInfo.page,
          route: this.systemInfo.route,
          userAgent: navigator.userAgent,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          status: 'new', // For admin management: new, reviewed, resolved, etc.
        }

        // Save to Firestore
        await db.collection('feedback').add(submissionData)

        // Update localStorage with submission time
        localStorage.setItem(lastSubmissionKey, now.toString())

        // Log for debugging (can remove later)
        console.log('Feedback submitted successfully:', submissionData)

        // Show success message
        Swal.fire({
          icon: 'success',
          title: this.$t('feedback.success.title'),
          text: this.$t('feedback.success.message'),
          timer: 3000,
          showConfirmButton: false,
        })

        // Reset form
        this.resetForm()
        this.closeDrawer()
      } catch (error) {
        console.error('Error submitting feedback:', error)
        Swal.fire({
          icon: 'error',
          title: this.$t('feedback.error.title'),
          text: this.$t('feedback.error.message'),
        })
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      this.formData = {
        name: '',
        email: '',
        category: null,
        rating: 0,
        description: '',
        screenshot: null,
        screenshotBase64: null,
      }
      this.$refs.form?.resetValidation()
    },
    closeDrawer() {
      this.$emit('update:modelValue', false)
    },
    onDrawerChange(value) {
      // Save state to localStorage
      localStorage.setItem('feedback_drawer_open', value ? 'true' : 'false')
      // Emit to parent to sync
      this.$emit('update:modelValue', value)
    },
  },
}
</script>

<style scoped>
.feedback-drawer {
  z-index: 999;
}

.feedback-drawer.top-38 {
  top: 38px;
}

/* Ensure expansion panel respects container padding to match other form fields */
/* Override global styles that set padding-left: 0 on expansion panels */
.feedback-drawer :deep(.v-expansion-panels) {
  margin-left: 0;
  margin-right: 0;
}

.feedback-drawer :deep(.v-expansion-panel-title) {
  padding-left: 16px !important; /* Match container pa-4 padding */
}

.feedback-drawer :deep(.v-expansion-panel-text__wrapper) {
  padding: 16px !important; /* Match container pa-4 padding */
}

@media (max-width: 960px) {
  .feedback-drawer.mobile-bottom-drawer {
    height: 80vh !important;
    top: auto !important;
    bottom: 0 !important;
  }
}

@media print {
  .feedback-drawer {
    display: none !important;
  }
}
</style>

